import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { lookupApi } from './lookupApi';
import './lookup.css';

const PAGE_SIZE_OPTIONS = [100, 250, 500];

const EMPTY_FORM = {
  code: '',
  title: '',
  description: '',
  category: '',
  source: '',
  version: '',
  effectiveDate: '',
  relatedRules: '',
  graphTriggers: '',
  denialRelevance: '',
  clinicalEvidenceNeeded: '',
  codingEvidenceNeeded: '',
  appealLogicHints: '',
  metadataText: '',
};

const summarizeMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') return '';
  const entries = Object.entries(metadata).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return entries
    .slice(0, 2)
    .map(([k, v]) => {
      if (Array.isArray(v)) return `${k}: ${v.length}`;
      if (typeof v === 'object') return `${k}: …`;
      return `${k}: ${String(v).slice(0, 18)}`;
    })
    .join(' · ');
};

const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

/**
 * Shared lookup list + CRUD table. Scope is set by the `caseId` prop:
 *   - null/undefined -> account-global lookup data
 *   - a case UUID    -> that case's rows only
 *
 * Props:
 *   caseId      -- scope (string | null)
 *   canWrite    -- enable add/edit/delete controls
 *   reloadKey   -- bump to force a reload (e.g. after an external upload)
 */
export default function LookupTable({ caseId = null, canWrite = false, reloadKey = 0 }) {
  const scope = useMemo(() => ({ caseId: caseId || undefined }), [caseId]);

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showMetadataEditor, setShowMetadataEditor] = useState(false);
  const [metadataError, setMetadataError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const hasFilter = useMemo(() => Boolean(category) || Boolean(search), [category, search]);
  const totalPages = useMemo(
    () => (totalCount > 0 ? Math.ceil(totalCount / pageSize) : 0),
    [totalCount, pageSize],
  );

  const loadCategories = useCallback(async () => {
    try {
      const resp = await lookupApi.categories(scope);
      if (resp?.success) setCategories(resp.data || []);
    } catch (err) {
      // Non-fatal.
      console.error('Failed to load lookup categories', err);
    }
  }, [scope]);

  const loadItems = useCallback(async () => {
    if (!hasFilter) {
      setItems([]);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await lookupApi.list({
        ...scope,
        category: category || undefined,
        search: search || undefined,
        page,
        pageSize,
      });
      if (resp?.success) {
        setItems(resp.data?.items || []);
        setTotalCount(resp.data?.totalCount || 0);
      } else {
        setError(resp?.message || 'Failed to load lookup records');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load lookup records');
    } finally {
      setLoading(false);
    }
  }, [scope, category, search, page, pageSize, hasFilter]);

  useEffect(() => { loadCategories(); }, [loadCategories, reloadKey]);
  useEffect(() => { loadItems(); }, [loadItems, reloadKey]);

  const applySearchDebounced = useMemo(
    () => debounce((value) => { setSearch(value); setPage(1); }, 350),
    [],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    applySearchDebounced(value);
  };

  const handleCategoryChange = (e) => { setCategory(e.target.value); setPage(1); };
  const handlePageSizeChange = (e) => { setPageSize(Number(e.target.value) || 100); setPage(1); };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_FORM, category: category || '' });
    setMetadataError(null);
    setShowMetadataEditor(false);
    setShowFormModal(true);
  };

  const openEditModal = (record) => {
    setEditingId(record.lookupId);
    setFormData({
      code: record.code || '',
      title: record.title || '',
      description: record.description || '',
      category: record.category || '',
      source: record.source || '',
      version: record.version || '',
      effectiveDate: record.effectiveDate ? record.effectiveDate.slice(0, 10) : '',
      relatedRules: record.relatedRules || '',
      graphTriggers: record.graphTriggers || '',
      denialRelevance: record.denialRelevance || '',
      clinicalEvidenceNeeded: record.clinicalEvidenceNeeded || '',
      codingEvidenceNeeded: record.codingEvidenceNeeded || '',
      appealLogicHints: record.appealLogicHints || '',
      metadataText: record.metadata && Object.keys(record.metadata).length > 0
        ? JSON.stringify(record.metadata, null, 2)
        : '',
    });
    setMetadataError(null);
    setShowMetadataEditor(Boolean(record.metadata && Object.keys(record.metadata).length > 0));
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setShowFormModal(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
  };

  const handleFieldChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.code.trim()) { setError('Code is required'); return; }
    if (!formData.description.trim()) { setError('Description is required'); return; }

    let metadataPayload;
    const metaTxt = (formData.metadataText || '').trim();
    if (metaTxt) {
      try {
        const parsed = JSON.parse(metaTxt);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Metadata must be a JSON object');
        }
        metadataPayload = parsed;
        setMetadataError(null);
      } catch (parseErr) {
        setMetadataError(parseErr.message || 'Invalid JSON');
        setShowMetadataEditor(true);
        return;
      }
    } else if (editingId) {
      metadataPayload = {};
    }

    setSaving(true);
    setError(null);
    try {
      const { metadataText, ...rest } = formData;
      const payload = { ...rest };
      if (!payload.effectiveDate) payload.effectiveDate = null;
      if (metadataPayload !== undefined) payload.metadata = metadataPayload;

      const resp = editingId
        ? await lookupApi.update(editingId, payload, scope)
        : await lookupApi.create(payload, scope);

      if (resp?.success) {
        setSuccess(editingId ? 'Lookup record updated' : 'Lookup record created');
        setShowFormModal(false);
        setEditingId(null);
        setFormData(EMPTY_FORM);
        await Promise.all([loadItems(), loadCategories()]);
      } else {
        setError(resp?.message || 'Failed to save record');
      }
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err.message;
      if (status === 409) {
        setError(message || 'A record with this code and category already exists in this scope');
      } else {
        setError(message || 'Failed to save record');
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      const resp = await lookupApi.remove(deleteTarget.lookupId, scope);
      if (resp?.success) {
        setSuccess('Lookup record deleted');
        setDeleteTarget(null);
        await Promise.all([loadItems(), loadCategories()]);
      } else {
        setError(resp?.message || 'Failed to delete record');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to delete record');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  return (
    <>
      <div className="lookup-toolbar">
        <div>
          <label htmlFor="lookup-category">Category</label>
          <select id="lookup-category" value={category} onChange={handleCategoryChange}>
            <option value="">— Select a category —</option>
            {categories.map((c) => (
              <option key={c.category || '__blank__'} value={c.category}>
                {(c.category || '(uncategorized)')} ({c.recordCount})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="lookup-search">Search code / title / description</label>
          <input
            id="lookup-search"
            type="text"
            placeholder="Start typing to search…"
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>
        <div>
          <label htmlFor="lookup-page-size">Page size</label>
          <select id="lookup-page-size" value={pageSize} onChange={handlePageSizeChange}>
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {canWrite && (
          <div style={{ marginLeft: 'auto' }}>
            <button type="button" className="lookup-btn lookup-btn--primary" onClick={openCreateModal}>
              + Add Record
            </button>
          </div>
        )}
      </div>

      {error && <div className="lookup-banner lookup-banner--error">{error}</div>}
      {success && <div className="lookup-banner lookup-banner--success">{success}</div>}

      {!hasFilter ? (
        <div className="lookup-empty">
          <strong>No records shown</strong>
          Select a category or enter a search term to view lookup records. This view does not
          auto-load — a scope can hold thousands of records.
        </div>
      ) : loading ? (
        <div className="lookup-banner lookup-banner--info">Loading lookup records…</div>
      ) : items.length === 0 ? (
        <div className="lookup-empty">
          <strong>No records match</strong>
          Try a different category or search term{canWrite ? ', or add a new record.' : '.'}
        </div>
      ) : (
        <>
          <div className="lookup-table-wrap">
            <table className="lookup-table">
              <thead>
                <tr>
                  <th style={{ width: '120px' }}>Code</th>
                  <th style={{ width: '220px' }}>Title</th>
                  <th>Description</th>
                  <th style={{ width: '160px' }}>Category</th>
                  <th style={{ width: '160px' }}>Meta</th>
                  <th style={{ width: '110px' }}>Effective</th>
                  {canWrite && <th style={{ width: '140px' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const metaChip = summarizeMetadata(row.metadata);
                  return (
                    <tr key={row.lookupId}>
                      <td className="lookup-code">{row.code}</td>
                      <td>{row.title || ''}</td>
                      <td>
                        <div className="lookup-desc" title={row.description || ''}>
                          {row.description || ''}
                        </div>
                      </td>
                      <td>{row.category || ''}</td>
                      <td
                        style={{ fontSize: 11, color: '#4b5563' }}
                        title={metaChip ? JSON.stringify(row.metadata, null, 2) : ''}
                      >
                        {metaChip}
                      </td>
                      <td>{row.effectiveDate ? row.effectiveDate.slice(0, 10) : ''}</td>
                      {canWrite && (
                        <td className="lookup-actions-cell">
                          <button type="button" className="lookup-icon-btn" onClick={() => openEditModal(row)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="lookup-icon-btn lookup-icon-btn--danger"
                            onClick={() => setDeleteTarget(row)}
                          >
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="lookup-pagination">
            <span>
              Page {page} of {totalPages || 1} — {totalCount.toLocaleString()} record
              {totalCount === 1 ? '' : 's'} total
            </span>
            <div className="lookup-pagination__controls">
              <button
                type="button"
                className="lookup-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="lookup-btn"
                onClick={() => setPage((p) => p + 1)}
                disabled={totalPages <= page}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {showFormModal && (
        <div className="lookup-modal-overlay" onClick={closeFormModal} role="dialog" aria-modal="true">
          <div className="lookup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lookup-modal__header">
              <h2>{editingId ? 'Edit Lookup Record' : 'Add Lookup Record'}</h2>
              <button type="button" className="lookup-modal__close" onClick={closeFormModal} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="lookup-modal__body">
                <div className="lookup-form-grid">
                  <div className="lookup-form-field">
                    <label htmlFor="lf-code">Code<span className="req">*</span></label>
                    <input id="lf-code" type="text" value={formData.code} onChange={handleFieldChange('code')} required maxLength={100} />
                  </div>
                  <div className="lookup-form-field">
                    <label htmlFor="lf-category">Category</label>
                    <input
                      id="lf-category"
                      type="text"
                      list="lookup-categories-datalist"
                      value={formData.category}
                      onChange={handleFieldChange('category')}
                      placeholder="Select existing or type a new one"
                      maxLength={200}
                    />
                    <datalist id="lookup-categories-datalist">
                      {categories.map((c) => <option key={c.category} value={c.category} />)}
                    </datalist>
                  </div>

                  <div className="lookup-form-field lookup-form-field--full">
                    <label htmlFor="lf-title">Title</label>
                    <input id="lf-title" type="text" value={formData.title} onChange={handleFieldChange('title')} maxLength={500} />
                  </div>

                  <div className="lookup-form-field lookup-form-field--full">
                    <label htmlFor="lf-description">Description<span className="req">*</span></label>
                    <textarea id="lf-description" value={formData.description} onChange={handleFieldChange('description')} rows={3} required />
                  </div>

                  <div className="lookup-form-field">
                    <label htmlFor="lf-source">Source</label>
                    <input id="lf-source" type="text" value={formData.source} onChange={handleFieldChange('source')} maxLength={500} />
                  </div>
                  <div className="lookup-form-field">
                    <label htmlFor="lf-version">Version</label>
                    <input id="lf-version" type="text" value={formData.version} onChange={handleFieldChange('version')} maxLength={100} />
                  </div>

                  <div className="lookup-form-field">
                    <label htmlFor="lf-effective-date">Effective Date</label>
                    <input id="lf-effective-date" type="date" value={formData.effectiveDate || ''} onChange={handleFieldChange('effectiveDate')} />
                  </div>
                  <div className="lookup-form-field">
                    <label htmlFor="lf-related-rules">Related Rules</label>
                    <input id="lf-related-rules" type="text" value={formData.relatedRules} onChange={handleFieldChange('relatedRules')} />
                  </div>

                  <div className="lookup-form-field lookup-form-field--full">
                    <label htmlFor="lf-graph-triggers">Graph Triggers</label>
                    <textarea id="lf-graph-triggers" value={formData.graphTriggers} onChange={handleFieldChange('graphTriggers')} rows={2} />
                  </div>
                  <div className="lookup-form-field lookup-form-field--full">
                    <label htmlFor="lf-denial-relevance">Denial Relevance</label>
                    <textarea id="lf-denial-relevance" value={formData.denialRelevance} onChange={handleFieldChange('denialRelevance')} rows={2} />
                  </div>
                  <div className="lookup-form-field lookup-form-field--full">
                    <label htmlFor="lf-clinical-evidence">Clinical Evidence Needed</label>
                    <textarea id="lf-clinical-evidence" value={formData.clinicalEvidenceNeeded} onChange={handleFieldChange('clinicalEvidenceNeeded')} rows={2} />
                  </div>
                  <div className="lookup-form-field lookup-form-field--full">
                    <label htmlFor="lf-coding-evidence">Coding Evidence Needed</label>
                    <textarea id="lf-coding-evidence" value={formData.codingEvidenceNeeded} onChange={handleFieldChange('codingEvidenceNeeded')} rows={2} />
                  </div>
                  <div className="lookup-form-field lookup-form-field--full">
                    <label htmlFor="lf-appeal-logic">Appeal Logic Hints</label>
                    <textarea id="lf-appeal-logic" value={formData.appealLogicHints} onChange={handleFieldChange('appealLogicHints')} rows={2} />
                  </div>

                  <div className="lookup-form-field lookup-form-field--full">
                    <button
                      type="button"
                      onClick={() => setShowMetadataEditor((v) => !v)}
                      style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: 13, textAlign: 'left' }}
                    >
                      {showMetadataEditor ? '▾' : '▸'} Metadata (JSON)
                    </button>
                    {showMetadataEditor && (
                      <div style={{ marginTop: 6 }}>
                        <textarea
                          id="lf-metadata"
                          value={formData.metadataText}
                          onChange={handleFieldChange('metadataText')}
                          rows={6}
                          spellCheck={false}
                          placeholder='{"mdc": "06", "drgType": "Surgical"}'
                          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
                        />
                        {metadataError && (
                          <div style={{ color: '#991b1b', fontSize: 12, marginTop: 4 }}>JSON error: {metadataError}</div>
                        )}
                        <div style={{ color: '#6b7280', fontSize: 11, marginTop: 4 }}>
                          Free-form JSON object stored alongside the record. Leave blank for none.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="lookup-modal__footer">
                <button type="button" className="lookup-btn" onClick={closeFormModal} disabled={saving}>Cancel</button>
                <button type="submit" className="lookup-btn lookup-btn--primary" disabled={saving}>
                  {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Create Record')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="lookup-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)} role="dialog" aria-modal="true">
          <div className="lookup-modal lookup-modal--small" onClick={(e) => e.stopPropagation()}>
            <div className="lookup-modal__header">
              <h2>Delete Lookup Record</h2>
              <button type="button" className="lookup-modal__close" onClick={() => !deleting && setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <div className="lookup-modal__body">
              <p className="lookup-delete-text">
                Are you sure you want to delete <code>{deleteTarget.code}</code>
                {deleteTarget.category ? <> in category <strong>{deleteTarget.category}</strong></> : null}? This cannot be undone.
              </p>
            </div>
            <div className="lookup-modal__footer">
              <button type="button" className="lookup-btn" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button type="button" className="lookup-btn lookup-btn--danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
